import React from 'react';
import * as yup from 'yup';

import { ROUTES_INTERNAL } from '@/utils/constants';

import {
  EventStates,
  NavigationItem,
  RouterType,
  VersionStates,
} from '../Providers/GraphqlProvider/generated';

const GLOBAL_DATE = new Date();

export const truncateTextTwoLines = (text: string, maxCharactersPerLine = 15) => {
  const words = text.split(' ');
  let line1 = '';
  let line2 = '';

  for (let i = 0; i < words.length; i++) {
    const currentLineLength = line1.length + (line1 !== '' ? 1 : 0) + words[i].length;
    if (currentLineLength <= maxCharactersPerLine) {
      line1 += (line1 !== '' ? ' ' : '') + words[i];
    } else {
      line2 += (line2 !== '' ? ' ' : '') + words[i];
    }
  }

  return (
    <div>
      <p>{line1}</p>
      <p>{line2}</p>
    </div>
  );
};

export function removeProperty(
  obj: { [key: string]: any },
  property: string
): { [key: string]: any } {
  if (obj.hasOwnProperty(property)) {
    delete obj[property];
  }
  return obj;
}

export function adaptFile(fileComponents: any[], dataToSubmit: any) {
  const files: any[] = [];
  for (const { variable } of fileComponents) {
    dataToSubmit[variable] && files.push(dataToSubmit[variable]);
    dataToSubmit = removeProperty(dataToSubmit, variable);
  }
  return files;
}

export const removeZero = (str: string) => {
  if (str.charAt(0) === '0') {
    return str.substring(1).replace('0', '.');
  }
  return str;
};

function returnTag(e: any) {
  const tag = e.srcElement ? e.srcElement.tagName : e.target.type;
  return tag;
}

export function returnText(e: any) {
  if (
    returnTag(e) == 'P' ||
    returnTag(e) == 'H1' ||
    returnTag(e) == 'H2' ||
    returnTag(e) == 'H3' ||
    returnTag(e) == 'H4' ||
    returnTag(e) == 'H5' ||
    returnTag(e) == 'H6' ||
    returnTag(e) == 'A' ||
    returnTag(e) == 'LI' ||
    returnTag(e) == 'SPAN' ||
    returnTag(e) == 'STRONG' ||
    returnTag(e) == 'BUTTON' ||
    returnTag(e) == 'SELECT' ||
    returnTag(e) == 'LABEL' ||
    returnTag(e) == 'TD' ||
    returnTag(e) == 'B' ||
    returnTag(e) == 'FONT'
  ) {
    return e.target.textContent;
  } else if (returnTag(e) == 'DIV' && e.target.textContent.length <= 100) {
    return e.target.textContent;
  } else if (returnTag(e) == 'IMG' && e.target.getAttribute('alt')) {
    return e.target.getAttribute('alt');
  } else if (returnTag(e) == 'INPUT' && e.target.getAttribute('value')) {
    return e.target.getAttribute('value');
  } else if (returnTag(e) == 'A' && e.target.getAttribute('aria-label')) {
    return e.target.getAttribute('aria-label');
  } else if (returnTag(e) == 'INPUT' && e.target.getAttribute('placeholder')) {
    return e.target.getAttribute('placeholder');
  } else {
    return '';
  }
}

export function compareLinkUrlAndRoute(route: string, link: NavigationItem | undefined | null) {
  if (!link) {
    return false;
  }

  if (
    link?.linkItem?.routeNavigation &&
    link?.linkItem?.routeType == RouterType.InternaltRoute &&
    !(link?.children?.length > 0) &&
    route.toLowerCase() == ROUTES_INTERNAL[link.linkItem.routeNavigation]?.toLowerCase()
  ) {
    return true;
  } else {
    if (!link.children?.length || link.children.length < 1) {
      return false;
    }

    return checkIsSomeNavigationBar(link, route);
  }
}

function checkIsSomeNavigationBar(link: NavigationItem, route: string): boolean {
  if (!link.children || link?.children?.length == 0) {
    return false;
  }

  return link.children.some((item) => {
    if (
      item?.linkItem?.url &&
      item?.linkItem?.url.trim() != '' &&
      item?.linkItem?.routeType != RouterType.InternaltRoute &&
      item.linkItem.url.trim() != '/' &&
      route.toLowerCase().includes(item.linkItem.url.toLowerCase().trim())
    ) {
      return true;
    } else if (
      item.linkItem?.routeType &&
      item.linkItem.routeType == RouterType.InternaltRoute &&
      item?.linkItem?.routeNavigation &&
      !(item?.children?.length > 0) &&
      ROUTES_INTERNAL[item.linkItem.routeNavigation] &&
      ROUTES_INTERNAL[item.linkItem.routeNavigation] != '/' &&
      route.toLowerCase().includes(ROUTES_INTERNAL[item.linkItem.routeNavigation].toLowerCase())
    ) {
      return true;
    }

    if (item.children && item.children.length > 0) {
      return checkIsSomeNavigationBar(item, route);
    } else {
      return false;
    }
  });
}

export function getEventStatus(state: EventStates | undefined | null) {
  switch (state) {
    case EventStates.Active:
      return 'Activo';
    case EventStates.Draft:
      return 'Borrador';
    case EventStates.Inactive:
      return 'Inactivo';
    default:
      return '';
  }
}

type YearBounds = {
  firstDay: Date;
  lastDay: Date;
};

export function getYearBounds(): YearBounds {
  const currentYear = new Date().getFullYear();
  const firstDay = new Date(currentYear, 0, 1, 0, 0, 0, 0);
  const lastDay = new Date(currentYear, 11, 31, 23, 59, 59, 999);

  return {
    firstDay,
    lastDay,
  };
}

export function getDateWithoutTimezone(date: Date) {
  const dd = new Date(date.getTime());
  dd.setHours(date.getHours() - GLOBAL_DATE.getTimezoneOffset() / 60);
  return dd;
}

export function formatToColombianCurrency(value: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function groupElementsByType(list: any[]) {
  if (!list || !Array.isArray(list)) {
    return [];
  }

  const result: any[] = [];
  let currentGroup: any[] = [];

  list.map((item: any, index: number) => {
    if (index === 0) {
      currentGroup.push(item);
    } else if (item.type == 'tabGroup') {
      result.push(currentGroup);
      result.push(item);
      currentGroup = [];
    } else {
      currentGroup.push(item);
    }
  });

  if (currentGroup.length > 0) {
    result.push(currentGroup);
  }

  return result;
}

export const buildYupSchema = (formData: any) => {
  if (!formData) {
    return yup.object();
  }
  //@ts-ignore
  const schemaFields = {} as any;

  formData.map((field: any) => {
    let fieldSchema;

    if (field?.properties?.inputType == 'file') {
      schemaFields[field.key] = yup.object();
      return;
    }

    switch (field.type) {
      case 'number':
        fieldSchema = yup.number().typeError('Debe ser un campo numérico');
        break;
      case 'check':
        fieldSchema = yup.number().typeError('Debe ser un campo numérico');
        break;
      case 'textfield':
      case 'textarea':
      case 'radio':
        fieldSchema = yup.string();
        break;
      case 'taglist':
        fieldSchema = yup.array();
        break;
      case 'datetime':
        fieldSchema = yup.date().typeError('Debe ser un campo tipo fecha');
        break;
      default:
        return field;
    }

    // field is validated if required or not
    if (field.validate && field.validate.required) {
      fieldSchema = fieldSchema.required('Este campo es obligatorio');
    } else {
      fieldSchema = fieldSchema.nullable();
    }

    //minLength of value is validated for string fields
    if (field.validate) {
      switch (field.type) {
        case 'textfield':
        case 'textarea':
          field.validate.minLength &&
            (fieldSchema = fieldSchema.min(
              field.validate.minLength,
              `Debe contener al menos ${field.validate.minLength} caracteres`
            ));
          field.validate.maxLength &&
            (fieldSchema = fieldSchema.max(
              field.validate.maxLength,
              `Debe contener menos de ${field.validate.maxLength} caracteres`
            ));
          break;
      }
    }

    //min value is validated for numeric fields
    if (field.type == 'number' && field.validate && field.validate.min != null) {
      fieldSchema = fieldSchema.min(field.validate.min, `El valor mínimo es ${field.validate.min}`);
    }

    //maxLength of value is validated for string fields
    // if (field.validate && field.validate.maxLength) {
    //   switch (field.type) {
    //     case 'textfield':
    //     case 'textarea':
    //       fieldSchema = fieldSchema.min(field.validate.maxLength, `Este campo tiene un máximo de ${field.validate.maxLength} caracteres`);
    //       break;
    //   }
    // } else {
    //   switch (field.type) {
    //     case 'textfield':
    //       fieldSchema = fieldSchema.max(200, 'Este campo tiene un máximo de 200 caracteres');
    //       break;
    //     case 'textarea':
    //       fieldSchema = fieldSchema.max(1000, 'Este campo tiene un máximo de 1000 caracteres');
    //       break;
    //   }
    // }

    //max value is validated for numeric fields
    if (field.type == 'number') {
      if (field.validate && field.validate.max != null) {
        fieldSchema = fieldSchema.min(
          field.validate.max,
          `El valor máximo es ${field.validate.max}`
        );
      } else {
        fieldSchema = fieldSchema.max(999999999999, `El valor máximo es 999999999999`);
      }
    }

    schemaFields[field.key] = fieldSchema;
  });

  return yup.object().shape(schemaFields);
};

export function cloneReactNode(node: React.ReactNode, additionalProps: any) {
  if (React.isValidElement(node)) {
    return React.cloneElement(node, additionalProps);
  }
  return node;
}

// Normaliza el nombre recibido para corregir codificaciones erróneas
export const normalizeFileName = (name: string) => {
  if (!name) return name;

  // 1) Intentar decodeURIComponent (por si viene percent-encoded)
  try {
    const decoded = decodeURIComponent(name);
    if (decoded && decoded !== name) return decoded;
  } catch {
    // ignore
  }

  // 2) Detectar si hay mojibake antes de intentar reparar
  // Mojibake típico: caracteres Latin1 extendidos (0x80-0xFF) que representan bytes UTF-8
  // Ejemplo: "Ã¡" (mojibake) vs "á" (correcto)
  const hasPossibleMojibake = /[\x80-\xff]{2,}/.test(name) || /Ã[\x80-\xbf]/.test(name);

  if (hasPossibleMojibake) {
    try {
      const bytes = Uint8Array.from(Array.from(name).map((ch) => ch.charCodeAt(0)));
      const decoder = new TextDecoder('utf-8', { fatal: true });
      const fixed = decoder.decode(bytes);
      // Solo retornar si la decodificación produjo un resultado diferente y válido
      if (fixed && fixed !== name) return fixed;
    } catch {
      // Si falla la decodificación UTF-8, no es mojibake válido, retornar original
    }
  }

  return name;
};
